// Customs declarations dashboard section (RD-2777): shipment-count KPI + over-time chart +
// sortable top-customers table (the parent companies the declarant works for). Single perspective
// (no role switch) and deliberately price-free — customs declarants must never see costs.
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ApiContext } from '@api/api';
import { CustomsDeclarationStatisticsDTO, CustomsDeclarationTopPartnerRowDTO } from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { formatNumber } from '@utils/utils.tsx';
import { useDashboardPref } from '../dashboardPrefs.tsx';
import SvgCustoms from '@icons/Customs.svg?react';
import DashboardSelect from '../components/DashboardSelect.tsx';
import DashboardSection from '../components/DashboardSection.tsx';
import DashboardKpiCard from '../components/DashboardKpiCard.tsx';
import DashboardTable, { DashboardColumn } from '../components/DashboardTable.tsx';
import DashboardCard from '../components/DashboardCard.tsx';
import DashboardChartTitle from '../components/DashboardChartTitle.tsx';
import RingilChart from '../charts/RingilChart.tsx';
import { axisTooltipFormatter, barValueLabel, chartColors } from '../charts/chartTheme.ts';
import { isChartDataEmpty, timeSeriesZoom } from '../charts/chartOptions.ts';
import { CustomsDeclarationTopSortBy, SortDir, StatsRange } from '../dashboardTypes.ts';

const CustomsDeclarationsSection: React.FC = () => {
  const { tr } = useTranslationLgs();
  const { getDashboardSectionCustomsDeclarations, getDashboardSectionCustomsDeclarationsTopPartners } = useContext(ApiContext);

  // Shared across all sections so the 30 days / 6 months switch ties them together.
  const [range, setRange] = useDashboardPref<StatsRange>('dashboard.range', StatsRange.D30);
  const [sortBy, setSortBy] = useDashboardPref<CustomsDeclarationTopSortBy>('customsDeclarations.sortBy', CustomsDeclarationTopSortBy.SHIPMENTS);
  const [sortDir, setSortDir] = useDashboardPref<SortDir>('customsDeclarations.sortDir', SortDir.DESC);

  const [stats, setStats] = useState<CustomsDeclarationStatisticsDTO>();
  const [partners, setPartners] = useState<CustomsDeclarationTopPartnerRowDTO[]>([]);

  useEffect(() => {
    getDashboardSectionCustomsDeclarations(range, data => setStats(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  useEffect(() => {
    getDashboardSectionCustomsDeclarationsTopPartners(range, sortBy, sortDir, data => setPartners(data ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, sortBy, sortDir]);

  const onSort = (key: CustomsDeclarationTopSortBy) => {
    if (key === sortBy) setSortDir(sortDir === SortDir.ASC ? SortDir.DESC : SortDir.ASC);
    else {
      setSortBy(key);
      setSortDir(SortDir.DESC);
    }
  };

  const comparison = range === StatsRange.D30 ? tr('dashboard.vsPrior30Days', 'vs. prior 30 days') : tr('dashboard.vsPrior6Months', 'vs. prior 6 months');
  const countLabel = tr('dashboard.customsDeclarations.count', 'Shipments');

  const overTimeOption = useMemo(() => {
    const ot = stats?.overTime ?? [];
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: axisTooltipFormatter(v => formatNumber(v, 0)),
      },
      legend: { top: 0, right: 0, data: [countLabel] },
      grid: { left: 0, right: 8, top: 40, bottom: 56, containLabel: true },
      ...timeSeriesZoom,
      xAxis: { type: 'category', data: ot.map(b => b.bucket) },
      yAxis: { type: 'value' },
      series: [
        {
          name: countLabel,
          type: 'bar',
          color: chartColors.success,
          data: ot.map(b => b.count),
          barMaxWidth: 40,
          label: barValueLabel(v => formatNumber(v, 0)),
        },
      ],
    };
  }, [stats, countLabel]);

  const overTimeEmpty = isChartDataEmpty((stats?.overTime ?? []).map(b => b.count));

  const columns: DashboardColumn<CustomsDeclarationTopPartnerRowDTO, CustomsDeclarationTopSortBy>[] = [
    { key: 'name', label: tr('dashboard.customsDeclarations.customer', 'Customer'), sortKey: CustomsDeclarationTopSortBy.NAME, render: r => r.companyName ?? '—' },
    { key: 'shipments', label: countLabel, sortKey: CustomsDeclarationTopSortBy.SHIPMENTS, align: 'right', render: r => formatNumber(r.shipments, 0) },
  ];

  const controls = (
    <DashboardSelect
      value={range}
      onChange={setRange}
      options={[
        { code: StatsRange.D30, label: tr('dashboard.range.d30', '30 days') },
        { code: StatsRange.M6, label: tr('dashboard.range.m6', '6 months') },
      ]}
    />
  );

  return (
    <DashboardSection title={tr('dashboard.customsDeclarations.title', 'Customs declarations')} icon={<SvgCustoms />} controls={controls}>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <DashboardKpiCard
          label={tr('dashboard.customsDeclarations.totalShipments', 'Total shipments')}
          value={formatNumber(stats?.totalShipments?.current ?? 0, 0)}
          changePct={stats?.totalShipments?.changePct}
          comparisonLabel={comparison}
          tooltip={tr('dashboard.customsDeclarations.totalShipmentsTooltip', 'Number of shipments with your customs declaration in the selected period.')}
        />
      </div>

      <DashboardCard>
        <DashboardChartTitle title={tr('dashboard.customsDeclarations.chartTitle', 'Shipments over time')} />
        <RingilChart option={overTimeOption} height={300} notMerge isEmpty={overTimeEmpty} />
      </DashboardCard>

      <DashboardCard>
        <DashboardChartTitle title={tr('dashboard.customsDeclarations.topCustomers', 'Top customers')} />
        <DashboardTable columns={columns} rows={partners} rowKey={(r, i) => r.companyId ?? `${i}`} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
      </DashboardCard>
    </DashboardSection>
  );
};

export default CustomsDeclarationsSection;
